import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import { FLOW_TYPE, REDIRECT_SUFFIX } from '../../../framework/App/AppConstants';
import Button, { BUTTON_STYLE } from '../../../shared-components/Button/Button';
import { trackTransition } from '../../../framework/store/FPTI/FPTIActions';
import { redirectFlowAction } from '../../../framework/store/RedirectFlow/RedirectFlowActions';
import { spinnerOpen } from '../../../framework/components/FullPageSpinner/FullPageSpinnerActions';
import './CancelUrl.less';

const LOCALE_PREFIX = 'US/components/Footer/Footer';

export const COMPONENT_STYLE = {
    LINK_BUTTON: 'LINK_BUTTON', // Button underneath call to action buttons (continue, apply)
    X_BUTTON: 'X_BUTTON',
    FOOTER_LINK: 'FOOTER_LINK'
};

export class CancelUrl extends Component {
    static propTypes = {
        merchantName: PropTypes.string,
        feedbackOnCancel: PropTypes.bool,
        id: PropTypes.string,
        channel: PropTypes.string,
        fireFptiEvent: PropTypes.func.isRequired,
        doCancelRedirect: PropTypes.func.isRequired,
        isPayInFullNowAvailable: PropTypes.bool,
        isExistingPayPalUser: PropTypes.bool,

        // Passed in props
        componentType: PropTypes.oneOf(Object.values(COMPONENT_STYLE)),
        fptiEvent: PropTypes.shape({ // Convenience prop for FPTI tracking
            stateName: PropTypes.string,
            transitionName: PropTypes.string
        }),
        onClick: PropTypes.func,
        customCancelLinkMessage: PropTypes.string,
        enableEscapeHatch: PropTypes.bool,
        qualtricsInterceptId: PropTypes.string
    };

    static defaultProps = {
        componentType: COMPONENT_STYLE.FOOTER_LINK,
        isPayInFullNowAvailable: false,
        isExistingPayPalUser: false,
        customCancelLinkMessage: `${LOCALE_PREFIX}.applyForBML.cancelApplication`,
    };

    handleQualtricsEvent = (event, footerClicked) => {
        event?.persist?.();

        const { enableEscapeHatch, feedbackOnCancel, qualtricsInterceptId } = this.props;

        if (enableEscapeHatch && feedbackOnCancel) {
            capeLog && capeLog('CancelUrl, handleQualtricsEvent enabled via enableEscapeHatch & feedbackOnCancel');
            try {
                window.PAYPAL.analytics.qualtricsInterceptInterrupt(qualtricsInterceptId, 'creditapplyweb-application-cancel-interrupt-sept2021', () =>
                    this.handleClick(event, footerClicked)
                );
            } catch (error) {
                capeLog && capeLog(`Error with qualtricsInterceptInterrupt func, err=${error.message}`);
                this.handleClick(event, footerClicked);
            }
        } else {
            this.handleClick(event, footerClicked);
        }
    };

    handleClickLinkButton = event => {
        this.handleQualtricsEvent(event);
    };

    handleClickXButton = event => {
        this.handleQualtricsEvent(event);
    };

    handleClickFooterLink = event => {
        this.handleQualtricsEvent(event, true);
    };

    handleClick = (event, footerCancel) => {
        const { doCancelRedirect, fireFptiEvent, fptiEvent, onClick, isPayInFullNowAvailable, isExistingPayPalUser } = this.props;
        let cancelUri;

        if (footerCancel) {
            cancelUri = REDIRECT_SUFFIX.CANCEL_REDIRECT;
        } else {
            cancelUri = isPayInFullNowAvailable && isExistingPayPalUser ? REDIRECT_SUFFIX.PAY_IN_FULL_REDIRECT : REDIRECT_SUFFIX.CANCEL_REDIRECT;
        }
    
        onClick && onClick(event);

        if (fptiEvent) {
            fireFptiEvent(fptiEvent.stateName, fptiEvent.transitionName);
        }

        return doCancelRedirect(cancelUri);
    };

    render() {
        const { merchantName, componentType, id, customCancelLinkMessage } = this.props;

        let cancelComponent;

        switch (componentType) {
            case COMPONENT_STYLE.LINK_BUTTON: {
                const cancelLinkContent = customCancelLinkMessage;
                cancelComponent = (
                    <div className="CancelUrl link-button">
                        <Button
                            data-automation-id="cancel"
                            btnStyle={BUTTON_STYLE.LINK}
                            onClick={this.handleClickLinkButton}
                            id={id}
                        >
                            <FormattedMessage
                                id={cancelLinkContent}
                            />
                        </Button>
                    </div>
                );
                break;
            }
            case COMPONENT_STYLE.X_BUTTON:
                cancelComponent = (
                    <div className="CancelUrl x-button">
                        <Button
                            data-automation-id="cancel"
                            btnStyle={BUTTON_STYLE.UNSTYLED}
                            onClick={this.handleClickXButton}
                            id={id}
                        >
                            <FormattedMessage
                                id={`${LOCALE_PREFIX}.applyForBML.cancelApplication`}
                            />
                        </Button>
                    </div>
                );
                break;
            case COMPONENT_STYLE.FOOTER_LINK:
            default:
                cancelComponent = (
                    <div className="CancelUrl link">
                        <Button
                            data-automation-id="cancel"
                            btnStyle={BUTTON_STYLE.LINK}
                            onClick={this.handleClickFooterLink}
                            id={id}
                        >
                            {merchantName ? (
                                <FormattedMessage
                                    id={`${LOCALE_PREFIX}.applyForBML.cancelWithMerchant`}
                                    values={{ merchantName }}
                                />
                            ) : (
                                <FormattedMessage
                                    id={`${LOCALE_PREFIX}.applyForBML.cancelWithoutMerchant`}
                                />
                            )}
                        </Button>
                    </div>
                );
        }

        return cancelComponent;
    }
}

export const mapStateToProps = state => {
    return {
        merchantName: state.serverData.merchantName,
        feedbackOnCancel: _.get(state, 'serverData.elmo.feedbackOnCancel', false) === true,
        channel: state.serverData.channel,
        isPayInFullNowAvailable: _.get(state, 'serverData.isPayInFullNowAvailable'),
        isExistingPayPalUser: _.get(state, 'serverData.isExistingPayPalUser'),
        enableEscapeHatch: _.get(state, 'serverData.featureFlags.wireOnQualtricCancel'),
        qualtricsInterceptId: _.get(state, 'serverData.qualtricsInterceptId')
    };
};

export const mapDispatchToProps = dispatch => ({
    fireFptiEvent(stateName, transitionName) {
        dispatch(trackTransition(stateName, transitionName));
    },
    doCancelRedirect(actionUri) {
        dispatch(spinnerOpen());
        dispatch(redirectFlowAction(actionUri));
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(CancelUrl);
